import { CSSProperties, useEffect, useRef, useState } from 'react';
import { shouldUseDsoStructure } from '~/growthbook';
import { useAppSelector } from '~/hooks';
import ArrowIcon from '~/images/arrow-switch-horizontal.png';
import DefaultProfileImage from '~/images/defaultProfileImage.png';
import { Colors } from '~/themes/colors';
import '~/themes/job_summary.scss';
import '~/themes/payment.scss';
import { OversightAccount } from './OversightAccount';
import { BackToOversightAccount } from './BackToOversightAccount';

interface IProps {
  onOfficeClicked: (officeId: string | null) => void;
  onSearch: (terms: string) => void;
}

export const SelectProfilePopup = ({ onOfficeClicked, onSearch }: IProps) => {
  const childOffices = useAppSelector((state) => state.user.childOffices);
  const filteredChilds = useAppSelector((state) => state.user.filteredChilds);
  const masterOfficeId = useAppSelector((state) => state.user?.user?.id);

  const selectedChildOffice = useAppSelector((state) => state.user.selectedChildOffice);
  const selectedOfficeId = useAppSelector((state) => state.user.selectedOfficeId);

  const [showChangeOffice, setChangeOfficePopup] = useState(false);
  const [searchChildOffice, setSearchChildOffice] = useState('');

  const wrapperRef = useRef(null);

  function useOutsideAlerter(ref: any) {
    useEffect(() => {
      /**
       * Alert if clicked on outside of element
       */
      function handleClickOutside(event: any) {
        if (ref.current && !ref.current.contains(event.target)) {
          setChangeOfficePopup(false);
        }
      }
      // Bind the event listener
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        // Unbind the event listener on clean up
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }, [ref]);
  }

  useOutsideAlerter(wrapperRef);

  useEffect(() => {
    if (searchChildOffice) {
      onSearch(searchChildOffice);
    }
  }, [searchChildOffice]);

  const renderAdminLabel = (showAdminLabel: boolean) => {
    if (!shouldUseDsoStructure() && showAdminLabel) {
      return (
        <div
          style={{
            backgroundColor: Colors.secondary_500,
            borderRadius: 3,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginTop: -10,
            zIndex: 4,
            alignSelf: 'center',
            position: 'initial',
            width: 55,
            height: 20,
          }}
        >
          <div
            style={{
              color: Colors.white,
              fontFamily: 'Nunito',
              fontSize: 12,
              fontWeight: 900,
            }}
          >
            ADMIN
          </div>
        </div>
      );
    }
    return <></>;
  };

  const renderProfileIcon = (
    profileImage: string,
    heightWidthStyle: number,
    borderRadius: number,
    showAdminLabel: boolean,
  ) => {
    if (profileImage) {
      return (
        <div
          style={{
            justifyContent: 'center',
            alignItems: 'center',
            flexDirection: 'column-reverse',
            display: 'flex',
          }}
        >
          {renderAdminLabel(showAdminLabel)}
          <img
            alt="profile image"
            data-cy={`${selectedChildOffice?.office_name}-profile-image`.split(' ').join('-')}
            src={profileImage}
            style={
              {
                width: heightWidthStyle,
                height: heightWidthStyle,
                borderRadius,
                resizeMode: 'contain',
                zIndex: 0,
              } as CSSProperties
            }
          />
        </div>
      );
    }
    return (
      <div
        style={{
          justifyContent: 'center',
          alignItems: 'center',
          flexDirection: 'column-reverse',
          display: 'flex',
        }}
      >
        {renderAdminLabel(showAdminLabel)}

        <img
          src={DefaultProfileImage}
          style={
            {
              width: heightWidthStyle,
              height: heightWidthStyle,
              resizeMode: 'contain',
            } as CSSProperties
          }
          alt="profile image"
        />
      </div>
    );
  };

  const officeNameAddressHeader = () => (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        margin: 10,
        maxWidth: 250,
      }}
    >
      <div
        style={{
          fontSize: 18,
          color: Colors.neutral_600,
          fontFamily: 'Nunito',
          fontWeight: 'bold',
          marginBottom: 8,
        }}
        data-cy="selected-office-name"
      >
        {`${selectedChildOffice?.office_name || ''}`}
      </div>

      <div
        style={{
          fontFamily: 'Nunito',
          fontSize: 15,
          width: '90%',
          color: '#546e7a',
          marginTop: 5,
        }}
      >
        {selectedChildOffice?.address && selectedChildOffice?.address.length !== 0
          ? selectedChildOffice?.address[0]?.formatted_address
          : 'N/A'}
      </div>
    </div>
  );
  const listOffices = searchChildOffice !== '' && filteredChilds ? filteredChilds : childOffices;

  const renderOfficeList = () => {
    if (listOffices.length === 0) {
      return (
        <div
          style={{
            width: '100%',
            display: 'flex',
            margin: 10,
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <div
            style={{
              fontFamily: 'Nunito',
              fontWeight: 'bold',
              color: Colors.neutral_600,
              fontSize: 16,
            }}
            data-cy="no-offices"
          >
            There are no offices
          </div>
        </div>
      );
    }
    return listOffices
      .filter((o) => o.id !== selectedOfficeId)
      .map((office) => (
        <div
          key={office.id}
          style={{ width: '100%', cursor: 'pointer' }}
          onClick={() => {
            setChangeOfficePopup(!showChangeOffice);
            onOfficeClicked(office.id);
          }}
        >
          <div
            style={{
              display: 'flex',
              margin: 10,
              alignItems: 'center',
            }}
          >
            <div style={{ marginRight: 10 }}>
              {renderProfileIcon(office?.profile_image, 60, 30, masterOfficeId === office.id)}
            </div>
            <div>
              <div
                style={{
                  fontFamily: 'Nunito',
                  fontWeight: 'bold',
                  color:
                    !shouldUseDsoStructure() && masterOfficeId === office.id
                      ? Colors.primary_500
                      : Colors.neutral_600,
                  fontSize: 16,
                }}
                data-cy={`office-option-${office?.office_name?.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '')}`}
              >
                {office.office_name}
              </div>
              <div
                style={{
                  fontFamily: 'Nunito',
                  color: Colors.neutral_600,
                  margin: 2,
                  fontSize: 14,
                }}
              >
                {office.address && office.address.length !== 0
                  ? office.address[0]?.formatted_address
                  : 'N/A'}
              </div>
            </div>
          </div>
        </div>
      ));
  };
  const renderChangeOfficeView = () => (
    <div
      className="payment-form"
      style={{
        display: 'flex',
        flexDirection: 'column',
        minWidth: 330,
        backgroundColor: Colors.white,
        position: 'absolute',
        zIndex: 5,
        padding: 10,
        left: 20,
        borderRadius: '10px',
        maxHeight: 500,
        overflowY: 'auto',
        boxShadow: '0px 2px 4px rgba(0,0,0,0.5)',
      }}
      ref={wrapperRef}
      data-cy="office-dropdown-menu"
    >
      <div
        className="card-body"
        style={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          minHeight: 130,
        }}
        onClick={() => setChangeOfficePopup(!showChangeOffice)}
      >
        <div style={{ margin: 20 }}>
          {!selectedChildOffice && shouldUseDsoStructure() && <OversightAccount />}

          {selectedChildOffice &&
            renderProfileIcon(
              selectedChildOffice?.profile_image,
              80,
              40,
              selectedChildOffice?.id === masterOfficeId,
            )}
        </div>
        {selectedChildOffice && officeNameAddressHeader()}
      </div>
      <div
        style={{
          height: 1,
          backgroundColor: Colors.neutral_100,
          opacity: 0.5,
          marginBottom: 20,
        }}
      />
      <input
        type="text"
        placeholder="Enter office name"
        data-cy="search-office-name"
        onChange={(e) => {
          setChangeOfficePopup(true);
          setSearchChildOffice(e.target.value);
        }}
        value={searchChildOffice}
        style={{
          width: '80%',
          padding: 5,
          alignSelf: 'center',
          margin: 10,
          height: 35,
          border: `1px solid ${Colors.neutral_100}`,
          fontFamily: 'Nunito',
        }}
      />
      {shouldUseDsoStructure() && selectedChildOffice && (
        <BackToOversightAccount
          onClick={() => {
            setChangeOfficePopup(!showChangeOffice);
            onOfficeClicked(null);
          }}
        />
      )}
      {renderOfficeList()}
    </div>
  );

  return (
    <div
      style={{
        cursor: 'pointer',
        marginBottom: 20,
      }}
    >
      {showChangeOffice ? renderChangeOfficeView() : <></>}
      <div
        className="card-body"
        style={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          minHeight: 130,
        }}
        onClick={() => setChangeOfficePopup(!showChangeOffice)}
        data-cy="office-selector-trigger"
      >
        {!selectedChildOffice && shouldUseDsoStructure() && <OversightAccount />}
        <div style={{ margin: 20 }}>
          {selectedChildOffice &&
            renderProfileIcon(
              selectedChildOffice.profile_image,
              80,
              40,
              selectedChildOffice.id === masterOfficeId,
            )}
        </div>
        {selectedChildOffice && officeNameAddressHeader()}

        <div
          data-cy={`${selectedChildOffice?.office_name}`.split(' ').join('-')}
          style={{ paddingRight: 25, alignSelf: 'center', marginTop: -12 }}
        >
          <img alt="arrow" src={ArrowIcon} height={24} width={24} />
        </div>
      </div>
      <div
        style={{
          height: 1,
          width: '90%',
          backgroundColor: Colors.neutral_100,
          alignSelf: 'center',
        }}
      />
    </div>
  );
};
